import React, { useState, useMemo } from "react";
import BorderedSelect from "src/components/form-new/bordered-select";
import CommissionInvoicesTable from "../components/commission-invoices-table";
import CommissionInvoiceDrawer from "../components/commission-invoice-drawer";
import {
  useGetCommissionInvoicesQuery,
  useGetCommissionInvoiceByIdQuery,
  useGetCommissionReferralsQuery,
  useUpdateCommissionInvoiceIncludeInTotalMutation,
} from "../endpoints/commissions.endpoints";
import type { CommissionInvoice } from "../types/commissions.types";
import { formatCurrency } from "../../utils/billing-utils";

const PERIOD_TYPE_OPTIONS = [
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
];

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const month = i + 1;
  const label = new Date(2000, i, 1).toLocaleString("default", { month: "long" });
  return { value: String(month), label };
});

const YEARS = [2026, 2025, 2024];

const QUARTERS = [
  { label: "Q1 2026", year: 2026, quarter: 1 },
  { label: "Q4 2025", year: 2025, quarter: 4 },
  { label: "Q3 2025", year: 2025, quarter: 3 },
  { label: "Q2 2025", year: 2025, quarter: 2 },
  { label: "Q1 2025", year: 2025, quarter: 1 },
  { label: "Q4 2024", year: 2024, quarter: 4 },
  { label: "Q3 2024", year: 2024, quarter: 3 },
  { label: "Q2 2024", year: 2024, quarter: 2 },
  { label: "Q1 2024", year: 2024, quarter: 1 },
];

/** Parse paid date string to month (1–12) and year; returns null if missing/invalid */
function parsePaidDate(paidDate: string | undefined): { month: number; year: number } | null {
  if (!paidDate) return null;
  const d = new Date(paidDate);
  if (Number.isNaN(d.getTime())) return null;
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

function monthToQuarter(month: number): number {
  return Math.ceil(month / 3);
}

const InvoicesView: React.FC = () => {
  const { data: invoices = [], isLoading } = useGetCommissionInvoicesQuery();
  const { data: referrals = [] } = useGetCommissionReferralsQuery();
  const [periodType, setPeriodType] = useState<"month" | "quarter">("month");
  const [selectedMonth, setSelectedMonth] = useState("1");
  const [selectedYear, setSelectedYear] = useState("2026");
  const [selectedQuarter, setSelectedQuarter] = useState(QUARTERS[0].label);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<CommissionInvoice | null>(null);

  const { data: fullInvoice } = useGetCommissionInvoiceByIdQuery(selectedInvoice?.id ?? "", {
    skip: !drawerOpen || !selectedInvoice?.id,
  });
  const [updateIncludeInTotal] = useUpdateCommissionInvoiceIncludeInTotalMutation();

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      if (inv.status !== "paid") return false;
      const paid = parsePaidDate(inv.paidDate);
      if (!paid) return false;
      if (periodType === "month") {
        if (paid.month !== Number(selectedMonth) || paid.year !== Number(selectedYear)) {
          return false;
        }
      } else {
        const q = QUARTERS.find((x) => x.label === selectedQuarter);
        if (!q || paid.year !== q.year || monthToQuarter(paid.month) !== q.quarter) {
          return false;
        }
      }
      return true;
    });
  }, [invoices, periodType, selectedMonth, selectedYear, selectedQuarter]);

  const totalIncome = useMemo(() => {
    const included = filteredInvoices.filter((inv) => inv.includeInTotal !== false);
    const invoiceTotal = included.reduce((sum, inv) => sum + inv.amount, 0);
    const totalExpenses = included.reduce(
      (sum, inv) => sum + (inv.expenses ?? 0),
      0
    );
    return invoiceTotal - totalExpenses;
  }, [filteredInvoices]);

  const handleToggleIncludeInTotal = (invoice: CommissionInvoice, includeInTotal: boolean) => {
    updateIncludeInTotal({ id: invoice.id, includeInTotal });
    // Cache is updated optimistically so the invoice stays visible with toggle off/on
  };

  const handleRowClick = (invoice: CommissionInvoice) => {
    setSelectedInvoice(invoice);
    setDrawerOpen(true);
  };

  return (
    <div className="mt-3 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <BorderedSelect
            label="View by"
            options={PERIOD_TYPE_OPTIONS}
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value as "month" | "quarter")}
          />
          {periodType === "month" ? (
            <>
              <BorderedSelect
                label="Month"
                options={MONTH_OPTIONS}
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              />
              <BorderedSelect
                label="Year"
                options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              />
            </>
          ) : null}
          {periodType === "quarter" && (
            <BorderedSelect
              label="Quarter"
              options={QUARTERS.map((q) => ({ value: q.label, label: q.label }))}
              value={selectedQuarter}
              onChange={(e) => setSelectedQuarter(e.target.value)}
            />
          )}
        </div>
        <span className="text-sm text-gray-600">
          Total Income:
          {" "}
          <span className="font-semibold text-gray-900">
            {formatCurrency(totalIncome)}
          </span>
        </span>
      </div>
      <p className="text-xs text-gray-500">
        Showing only paid invoices, filtered by paid date.
      </p>
      {isLoading ? (
        <p className="text-sm text-gray-500 py-4">
          Loading invoices…
        </p>
      ) : (
        <CommissionInvoicesTable
          invoices={filteredInvoices}
          onRowClick={handleRowClick}
          onToggleIncludeInTotal={handleToggleIncludeInTotal}
        />
      )}
      <CommissionInvoiceDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        invoice={fullInvoice ?? selectedInvoice}
        referrals={referrals}
        onToggleIncludeInTotal={handleToggleIncludeInTotal}
      />
    </div>
  );
};

export default InvoicesView;
